import { useCallback, useEffect, useState } from "react";
import { useAuth } from "react-oidc-context";

import { createApiClient } from "../api/client";
import {
  auditListPath,
  eventsOf,
  type AuditDomain,
  type AuditPage,
} from "../api/mcp";
import type { AppConfig } from "../config";
import { classifyError, type Resource } from "./useResource";

/**
 * The fleet audit list, including `?cursor=` for the next page.
 *
 * `useResource` replaces its payload on every path change, so putting the
 * cursor in the path would throw away the rows already on screen. This hook
 * appends. The cursor itself is opaque — it is sent back exactly as received.
 */
export function useAuditFeed(config: AppConfig, domain: AuditDomain | "") {
  const auth = useAuth();
  const token = auth.user?.id_token;
  const [state, setState] = useState<Resource<AuditPage>>({ kind: "loading" });
  const [loadingMore, setLoadingMore] = useState(false);
  const [moreError, setMoreError] = useState<string | null>(null);

  const fetchPage = useCallback(
    async (cursor: string | null, append: boolean) => {
      const api = createApiClient({ apiUrl: config.apiUrl, getIdToken: () => token });
      const page = await api.request<AuditPage>(auditListPath(domain), {
        query: cursor ? { cursor } : undefined,
      });
      setState((prev) => {
        if (!append || prev.kind !== "ready") return { kind: "ready", data: page };
        return {
          kind: "ready",
          data: {
            events: [...eventsOf(prev.data), ...eventsOf(page)],
            cursor: page.cursor,
            count: (prev.data.count ?? 0) + (page.count ?? 0),
          },
        };
      });
    },
    [config.apiUrl, token, domain],
  );

  useEffect(() => {
    let active = true;
    setState({ kind: "loading" });
    setMoreError(null);
    fetchPage(null, false).catch((err: unknown) => {
      if (active) setState(classifyError(err));
    });
    return () => {
      active = false;
    };
  }, [fetchPage]);

  const reload = useCallback(() => {
    setState({ kind: "loading" });
    setMoreError(null);
    void fetchPage(null, false).catch((err: unknown) => setState(classifyError(err)));
  }, [fetchPage]);

  const loadMore = useCallback(() => {
    if (state.kind !== "ready" || !state.data.cursor || loadingMore) return;
    setLoadingMore(true);
    setMoreError(null);
    void fetchPage(state.data.cursor, true)
      .catch((err: unknown) => {
        const classified = classifyError(err);
        const message =
          classified.kind === "signedout"
            ? "Signed out"
            : "error" in classified
              ? classified.error.message
              : "Could not load more rows";
        setMoreError(message);
      })
      .finally(() => setLoadingMore(false));
  }, [fetchPage, loadingMore, state]);

  return { state, reload, loadMore, loadingMore, moreError };
}
